/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    IPayLoad,
    ITelemetryContent,
    NodeTag,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IAccordionData } from './accordion.data';
import { IAccordionProps } from './accordion.props.autogenerated';

import { createAccordionStateInput, AccordionStates } from '../../actions/get-accordion-state';


/**
 *
 * Accordion component.
 * @extends {React.PureComponent<IAccordionProps<IAccordionData>>}
 */

export interface ICallToActionProps {
    link: INodeProps[];
}

export interface IAccordionViewProps extends IAccordionProps<{}> {
    heading?: React.ReactNode;
    AccordionContainer: IModuleProps;
    HeaderSection: INodeProps;
    HeadingWrapper: INodeProps;
    accordianButtonsContainer: INodeProps;
    accordionButtons: React.ReactNode;
    accordionItemContainer: INodeProps;
    accordionItems: React.ReactNode;
}

/**
 *
 * Accordion component.
 * @extends {React.Component<IAccordionProps<IAccordionData>>}
 */
@observer
class Accordion extends React.PureComponent<IAccordionProps<IAccordionData>> {
    private readonly telemetryContent?: ITelemetryContent;

    private readonly payLoad: IPayLoad;

    private readonly expandAllButtonRef: React.RefObject<HTMLButtonElement> = React.createRef<HTMLButtonElement>();

    private readonly collapseAllButtonRef: React.RefObject<HTMLButtonElement> = React.createRef<HTMLButtonElement>();

    constructor(props: IAccordionProps<IAccordionData>) {
        super(props);
        this._onExpandAll = this._onExpandAll.bind(this);
        this._onCollapseAll = this._onCollapseAll.bind(this);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '');
    }

    public render(): JSX.Element {
        const { context, data, slots } = this.props;
        const { heading, className } = this.props.config;
        const headingComponent = heading && heading.text && (
            <Msdyn365.Text
                className='ms-accordion-header-section__heading'
                tag={heading.tag || 'h1'}
                text={heading.text}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );

        const accordionId = this.props.id;
        const accordionStateResult = data.accordionState?.result || {};

        const accordionViewProps = {
            ...this.props,
            AccordionContainer: {
                moduleProps: this.props,
                className: classnames('ms-accordion', className)
            },
            HeaderSection: { className: 'ms-accordion-header-section' },
            HeadingWrapper: { className: 'ms-accordion-header-section-container' },
            heading: headingComponent,
            config: this.props && this.props.config,
            accordianButtonsContainer: {
                tag: 'div' as NodeTag,
                className: 'ms-accordion-header-section-toggle-button-container'
            },
            accordionButtons: this._renderAccordionButton(context, accordionStateResult, accordionId),
            accordionItemContainer: {
                tag: 'div' as NodeTag,
                className: 'ms-accordion-item-section'
            },
            accordionItems:
                slots &&
                slots.accordionItems &&
                slots.accordionItems.length > 0 &&
                slots.accordionItems.map((item: React.ReactNode, index: number) => this._getAccordionItem(item, index))
        };
        return this.props.renderView(accordionViewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    private _renderAccordionButton(context: Msdyn365.ICoreContext, accordionStateResult: AccordionStates, accordionId: string): React.ReactNode | null {
        const { config, resources } = this.props;
        const { heading } = config;
        this.payLoad.contentAction.etext = TelemetryConstant.ExpandAll;
        const attributeExpandAll = getTelemetryAttributes(this.telemetryContent!, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.CollapseAll;
        const attributeCollapseAll = getTelemetryAttributes(this.telemetryContent!, this.payLoad);
        if (!config.showToggleAll) {
            this.props.context.telemetry.error('Accordion button content is empty, module wont render');
            return null;
        }
        return (
            <>
                <button
                    className='ms-accordion_ExpandAll'
                    aria-label={`${resources.expandAllText} ${heading?.text ? heading.text : ''}`}
                    onClick={() => this._onExpandAll(context, accordionStateResult, accordionId)}
                    ref={this.expandAllButtonRef}
                    {...attributeExpandAll}
                >
                    {resources.expandAllText}
                </button>
                &nbsp;
                {resources.toggleAllSeparator}
                &nbsp;
                <button
                    className='ms-accordion_CollapseAll'
                    aria-label={`${resources.collapseAllText} ${heading?.text ? heading.text : ''}`}
                    onClick={() => this._onCollapseAll(context, accordionStateResult, accordionId)}
                    ref={this.collapseAllButtonRef}
                    {...attributeCollapseAll}
                >
                    {resources.collapseAllText}
                </button>
            </>
        );
    }

    private _onExpandAll = (context: Msdyn365.ICoreContext, accordionStateResult: AccordionStates, accordionId: string): void => {
        updateAccordionStates(context, accordionStateResult, accordionId, true);
        resetAccordionStates(context, accordionStateResult, accordionId, true);
    };

    private _onCollapseAll = (context: Msdyn365.ICoreContext, accordionStateResult: AccordionStates, accordionId: string): void => {
        updateAccordionStates(context, accordionStateResult, accordionId, false);
        resetAccordionStates(context, accordionStateResult, accordionId, false);
    };

    private _getAccordionItem(item: React.ReactNode, index: number): React.ReactNode {
        return (
            <React.Fragment key={index}>
                {React.cloneElement(item as React.ReactElement, { interactionStyle: this.props.config.interactionStyle })}
            </React.Fragment>
        );
    }
}

export const updateAccordionStates = (context: Msdyn365.ICoreContext, result: AccordionStates, accordionId: string, isAllExpanded: boolean): void => {
    result[accordionId] ? result[accordionId].isAllExpanded = isAllExpanded : result[accordionId] = { isAllExpanded };
    context.actionContext.update(createAccordionStateInput(result), result);
};

export const resetAccordionStates = (context: Msdyn365.ICoreContext, result: AccordionStates, accordionId: string, isAllExpanded: boolean): void => {
    const accordionChildren = result[accordionId]?.children;
    if (accordionChildren) {
        Object.keys(accordionChildren).forEach(child => {
            updateAccordionState(context, result, accordionId, child, isAllExpanded);
        });
    }
};

export const updateAccordionState = (context: Msdyn365.ICoreContext, result: AccordionStates, accordionId: string, accordionItemId: string, isExpanded: boolean): void => {
    result[accordionId] ? (result[accordionId].children
        ? result[accordionId].children![accordionItemId] = isExpanded
        : result[accordionId].children = { [accordionItemId]: isExpanded }
    ) : (result[accordionId] = { children: { [accordionItemId]: isExpanded } });
    context.actionContext.update(createAccordionStateInput(result), result);
};

export default Accordion;
